import { useContext, useState, useCallback } from 'react';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Box,
  RadioGroup,
  FormControl,
  Typography,
  Divider,
  MenuItem,
  InputLabel,
} from '@material-ui/core';
import dayjs from 'dayjs';
import DateFnsUtils from '@date-io/date-fns';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';

import { MAIN_CONTAINER_ID } from '../../../../constants';
import { DeviceContext } from '../../../../contexts';
import { AppRadioButton } from '../../../../components/inputs/AppRadio';
import { monochrome } from '../../../../theme/styles';
import { AppKeyboardTimePicker } from '../../../../components/inputs/AppKeyboardTimePicker';
import { timeStringToDate, timeToSeconds } from '../../../../utils/dates';
import { SiteShift } from '../../../../types';
import { AppSelect } from '../../../../components/inputs/AppSelect';
import { bulkAmendTimeLogsRequest, bulkEditTimeLogsRequest } from '../../../../api/time';
import { ButtonTitleWithLoading } from '../../../../components/ButtonTitleWithLoading';
import { ActionTypeSwitch } from './ActionTypeSwitch';
import { AppTimeInput } from '../../../../components/inputs/AppTimeInput';

export const EditMultipleTimelogsDialog = ({
  open,
  handleClose,
  dates,
  selectedDate,
  handleDateChange,
  shifts,
  selectedShift,
  setSelectedShift,
  companyId,
  siteId,
  userId,
  onSuccess,
}: {
  open: boolean;
  handleClose: () => void;
  dates: string[];
  selectedDate: string;
  handleDateChange: (date: string) => void;
  shifts: SiteShift[];
  selectedShift: SiteShift | null;
  setSelectedShift: (shift: SiteShift | null) => void;
  companyId: string;
  siteId: string;
  userId: string;
  onSuccess: () => void;
}) => {
  const { isMobile } = useContext(DeviceContext);
  const styles = { width: 260, borderColor: monochrome.mediumlight };
  const [removeTime, setRemoveTime] = useState(false);
  const [startTime, setStartTime] = useState<string | null>(null);
  const [endTime, setEndTime] = useState<string | null>(null);
  const [editMode, setEditMode] = useState<'number' | 'time' | null>(null);
  const [loading, setLoading] = useState(false);
  const [regularHoursRemove, setRegularHoursRemove] = useState(false);
  const [regularHoursValue, setRegularHoursValue] = useState<string | null>(null);
  const [overtimeRemove, setOvertimeRemove] = useState(false);
  const [overtimeValue, setOvertimeValue] = useState<string | null>(null);

  const bulkEditTimeLogs = useCallback(async () => {
    if (!companyId || !siteId || !selectedDate || !selectedShift?.uuid || !startTime || !endTime) {
      return;
    }
    try {
      setLoading(true);
      const timeFrom = dayjs(`${selectedDate} ${startTime}`).toISOString();
      const timeTo = dayjs(`${selectedDate} ${endTime}`).toISOString();

      await bulkEditTimeLogsRequest({
        company_id: companyId,
        shift_id: selectedShift.uuid,
        workday: selectedDate,
        custom_periods: [
          {
            time_from: timeFrom,
            time_to: timeTo,
            hour_type: 'basic',
            exclude: removeTime,
          },
        ],
      });
      setLoading(false);
      handleClose();
      onSuccess();
    } catch (e) {
      setLoading(false);
      console.error(e);
    }
  }, [companyId, siteId, selectedDate, selectedShift, startTime, endTime, removeTime]);

  const bulkAmendTimeLogs = useCallback(async () => {
    if (!companyId || !siteId || !selectedDate || !selectedShift?.uuid) {
      return;
    }
    try {
      setLoading(true);
      await bulkAmendTimeLogsRequest({
        company_id: companyId,
        workday: selectedDate,
        user_id: userId,
        site_id: siteId,
        shift_id: selectedShift.uuid,
        payload: {
          basic_amendment: regularHoursValue ? timeToSeconds(regularHoursValue, regularHoursRemove) : 0,
          overtime_amendment: overtimeValue ? timeToSeconds(overtimeValue, overtimeRemove) : 0,
          paid_break_amendment: 0,
          unpaid_break_amendment: 0,
        },
      });
      handleClose();
      onSuccess();
      setLoading(false);
    } catch (e) {
      setLoading(false);
      console.error(e);
    }
  }, [
    companyId,
    siteId,
    selectedDate,
    selectedShift,
    regularHoursValue,
    overtimeValue,
    regularHoursRemove,
    overtimeRemove,
  ]);

  return (
    <Dialog
      container={document.getElementById(MAIN_CONTAINER_ID)}
      fullScreen={isMobile}
      maxWidth="md"
      onClose={() => handleClose()}
      open={open}>
      <DialogTitle>Edit multiple Time Logs</DialogTitle>

      <DialogContent style={{ width: isMobile ? '100%' : 800, marginTop: 16 }}>
        <Typography>Which date you would like to edit?</Typography>

        <FormControl component="fieldset" style={{ width: '100%' }}>
          <RadioGroup value={selectedDate || null} onChange={event => handleDateChange(event.target.value)}>
            <Box display="flex" m={1} width="100%" flexWrap="wrap">
              {dates.map(item => (
                <FormControlLabel
                  key={item}
                  value={item}
                  control={<AppRadioButton color="primary" name={dayjs(item).format('dddd DD')} />}
                  label=""
                />
              ))}
            </Box>
          </RadioGroup>
        </FormControl>

        <Typography style={{ marginTop: 16 }}>Which shift you would like to edit?</Typography>

        <FormControl fullWidth margin="normal" variant="filled">
          <InputLabel id="app-select-label">Select shift</InputLabel>
          <AppSelect
            labelId="app-select-label"
            id="shift-select"
            multiple={false}
            value={selectedShift}
            onChange={event => {
              setSelectedShift(event.target.value as SiteShift);
            }}>
            {shifts.map((item: any) => {
              return (
                <MenuItem key={item.name} value={item}>
                  {item.name}
                </MenuItem>
              );
            })}
          </AppSelect>
        </FormControl>

        <Typography style={{ marginTop: 16 }}>
          Would you like to simply add or remove hours (E.g. Add 60 minutes to all users) or add or remove a
          specific period between two times (E.g. Remove any clocked in paid time between 9.30am and
          10.15am)?
        </Typography>

        <Box display="flex" justifyContent="center" marginTop={2} gridGap={16}>
          <Button
            onClick={() => setEditMode('number')}
            size="large"
            style={styles}
            variant={editMode === 'number' ? 'contained' : 'outlined'}
            color="primary">
            Change by a number of hours
          </Button>

          <Button
            onClick={() => setEditMode('time')}
            size="large"
            style={styles}
            variant={editMode === 'time' ? 'contained' : 'outlined'}
            color="primary">
            Limit to a certain time
          </Button>
        </Box>

        {editMode && (
          <Box marginTop={3}>
            <Divider />

            {editMode === 'time' ? (
              <Box marginTop={3}>
                <Box display="flex" alignItems="center" gridGap={16}>
                  <Box width={200}>
                    <ActionTypeSwitch checked={removeTime} onChange={value => setRemoveTime(value)} />
                  </Box>

                  <Typography style={{ flex: 1 }}>Worked Hours during this time period:</Typography>
                </Box>

                <Box display="flex" alignItems="center" gridGap={16} marginTop={2}>
                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <AppKeyboardTimePicker
                      value={startTime ? timeStringToDate(startTime) : null}
                      onChange={data => setStartTime(data ? dayjs(data).format('HH:mm') : null)}
                      label="Start Time"
                      margin="none"
                      fullWidth
                      id="time-picker-dialog"
                      ampm={false}
                      mask="__:__"
                    />
                  </MuiPickersUtilsProvider>
                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <AppKeyboardTimePicker
                      value={endTime ? timeStringToDate(endTime) : null}
                      onChange={data => setEndTime(data ? dayjs(data).format('HH:mm') : null)}
                      label="End Time"
                      margin="none"
                      fullWidth
                      id="time-picker-dialog"
                      ampm={false}
                      mask="__:__"
                    />
                  </MuiPickersUtilsProvider>
                </Box>
              </Box>
            ) : (
              <Box marginTop={3} display="flex" alignItems="center" gridGap={16} width="100%">
                <Box flex={1}>
                  <Typography style={{ marginBottom: 8 }}>Regular Hours:</Typography>
                  <Box display="flex" alignItems="center" gridGap={16}>
                    <Box width={300}>
                      <ActionTypeSwitch
                        checked={regularHoursRemove}
                        onChange={value => setRegularHoursRemove(value)}
                      />
                    </Box>
                    <AppTimeInput
                      value={regularHoursValue || ''}
                      onChange={value => setRegularHoursValue(value)}
                      placeholder="HH:MM"
                    />
                  </Box>
                </Box>

                <Divider orientation="vertical" flexItem />

                <Box flex={1}>
                  <Typography style={{ marginBottom: 8 }}>Overtime Hours:</Typography>
                  <Box display="flex" alignItems="center" gridGap={16}>
                    <Box width={300}>
                      <ActionTypeSwitch
                        checked={overtimeRemove}
                        onChange={value => setOvertimeRemove(value)}
                      />
                    </Box>
                    <AppTimeInput
                      value={overtimeValue || ''}
                      onChange={value => setOvertimeValue(value)}
                      placeholder="HH:MM"
                    />
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button size="large" onClick={() => handleClose()} variant="outlined">
          Close
        </Button>

        <Button
          style={{ width: 100 }}
          size="large"
          disabled={!editMode || !selectedDate || loading}
          type="submit"
          color="primary"
          variant="contained"
          onClick={editMode === 'time' ? bulkEditTimeLogs : bulkAmendTimeLogs}>
          <ButtonTitleWithLoading title="Save" loaderVariant="paper" loading={loading} />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
